import styled, { keyframes, css } from "styled-components";

const fadeInScale = keyframes`
  0% { opacity: 0; transform: scale(0.9); }
  100% { opacity: 1; transform: scale(1); }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(4px) scale(0.98); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const shimmer = keyframes`
  0%   { transform: translateX(-150%) skewX(-20deg); }
  100% { transform: translateX(150%)  skewX(-20deg); }
`;

export const WrapperContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 0.428rem;
`;

export const BlurredContent = styled.div`
  filter: blur(3px);
  pointer-events: none;
  transition: filter 0.3s ease;
  width: 100%;
  user-select: none;
  -webkit-user-select: none;
  border-radius: 6rem !important;
`;

export const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 0px;
  background-color: rgba(0, 0, 0, 0.05);
  pointer-events: auto;
`;

export const ModalCard = styled.div`
  background-color: ${({ skin }) => (skin === "dark" ? "#283046" : "#fff")};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  padding: 24px 0 24px 0;
  max-width: ${({ modalWidth }) => modalWidth || "800px"};
  max-height: ${({ maxHeight }) => maxHeight || "auto"};
  width: 100%;
  display: flex;
  flex-direction: column;
  animation: ${fadeInScale} 0.4s ease-out;
  position: relative;
`;

export const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 2rem;
  margin-bottom: 20px;
  text-align: ${({ isCentered }) => (isCentered ? "center" : "left")};

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #aaa;
  }
`;

export const LockIconContainer = styled.div`
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 24px;
  width: 64px;
  height: 64px;
  overflow: hidden;

  svg {
    width: 64px;
    height: 64px;
    fill: #7367f0;
    animation: ${fadeIn} 1s ease-out both;
    will-change: transform, opacity;
    display: block;
    position: relative;
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background: ${({ skin }) =>
      skin === "dark"
        ? `linear-gradient(
      to right,
      rgba(115, 103, 240, 0) 0%,
      rgba(115, 103, 240, 0.6) 50%,
      rgba(115, 103, 240, 0) 100%
    )`
        : `linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.55) 50%,
      rgba(255, 255, 255, 0) 100%
    )`};
    filter: blur(2px);
    border-radius: 50%;
    ${({ skin }) =>
      skin === "dark"
        ? css`
            animation: none;
          `
        : css`
            animation: ${shimmer} 2s ease-out 3s infinite;
          `}
    transform: translateX(120%) skewX(0deg);
    z-index: 2;
  }
`;

export const StyledAvatar = styled.div`
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ skin }) =>
    skin === "dark"
      ? "linear-gradient(135deg, #7367f0 0%, #9e95f5 100%)"
      : "linear-gradient(135deg, #7367f0 0%, #9e95f5 100%)"};
  box-shadow: 0 4px 16px rgba(115, 103, 240, 0.3);
  overflow: hidden;
  animation: ${fadeIn} 1s ease-out both;

  svg {
    width: 26px;
    height: 26px;
    fill: #fff;
    position: relative;
    z-index: 1;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.55) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(3px);
    animation: ${shimmer} 2s ease-in-out 3s infinite;
    transform: translateX(-150%) skewX(-20deg);
    z-index: 2;
  }
`;

export const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: ${({ skin }) => (skin === "dark" ? "#d0d2d6" : "#2c3e50")};
  margin-bottom: 16px;
  line-height: 1.4;
`;

export const ModalDescription = styled.p`
  font-size: 16px;
  color: ${({ skin }) => (skin === "dark" ? "#b4b7bd" : "#6c757d")};
  margin-bottom: 32px;
  line-height: 1.6;
`;

export const CTAButton = styled.button`
  width: calc(100% - 48px);
  margin-left: 24px;
  margin-right: 24px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #7367f0 0%, #5e57e0 100%);
  color: #fff;
  border: none;
  border-radius: 50px;
  padding: 16px 40px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  z-index: 1;
  animation: ${fadeIn} 0.5s ease-out 0.05s both;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 0 36px 8px rgba(115, 103, 240, 0.4),
      0 4px 15px rgba(115, 103, 240, 0.48);
  }

  &:active {
    transform: scale(0.98);
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0%;
    width: 100%;
    pointer-events: none;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.55) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(3px);
    transform: translateX(-160%) skewX(-20deg);
    animation: ${shimmer} 2s ease-out 3s infinite;
    z-index: 2;
  }
`;

export const ButtonText = styled.span`
  position: relative;
  z-index: 3;
`;
